/**
 * Shared authenticated admin-image scheduler and object-URL pool.
 *
 * ADR-025 B-4 / E5: a 20-reply fixture produced 80 eager thumbnail reads and
 * 80 resident blob URLs. All admin thumbnails now acquire through this
 * reference-counted pool so identical attachment+variant reads share one blob,
 * at most eight network reads run concurrently, and the final release revokes
 * the object URL. The token participates in the private key so bytes are never
 * shared across authenticated sessions; keys are never logged and disappear
 * with the last lease.
 */

import { fetchAdminImage } from '../../api/client';

export const ADMIN_IMAGE_MAX_CONCURRENT = 8;

interface ScheduledTask<T> {
  run: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (reason: unknown) => void;
}

const waiting: ScheduledTask<unknown>[] = [];
let active = 0;

function pump(): void {
  while (active < ADMIN_IMAGE_MAX_CONCURRENT && waiting.length > 0) {
    const task = waiting.shift();
    if (!task) return;
    active += 1;
    void task
      .run()
      .then(task.resolve, task.reject)
      .finally(() => {
        active -= 1;
        pump();
      });
  }
}

function schedule<T>(run: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    waiting.push({ run, resolve, reject } as ScheduledTask<unknown>);
    pump();
  });
}

interface ResourceEntry {
  refs: number;
  promise: Promise<string>;
  url: string | null;
  /** Invalidates a queued final-release revocation when a consumer remounts. */
  revokeGeneration: number;
}

const resources = new Map<string, ResourceEntry>();

export interface AdminImageLease {
  readonly url: Promise<string>;
  release: () => void;
}

function resourceKey(token: string, src: string): string {
  return `${token}\u0000${src}`;
}

/**
 * Revoke at the next microtask checkpoint, rather than inside the releasing
 * effect cleanup. React may unmount and remount a table cell in one commit even
 * though the attachment remains visible. Giving that replacement effect one
 * microtask to reacquire the entry prevents an unnecessary authenticated read
 * without retaining an actually unused blob beyond that checkpoint.
 */
function scheduleRevokeIfUnused(key: string, entry: ResourceEntry): void {
  if (entry.refs !== 0 || entry.url === null || resources.get(key) !== entry) {
    return;
  }

  const generation = ++entry.revokeGeneration;
  queueMicrotask(() => {
    if (
      entry.revokeGeneration !== generation ||
      entry.refs !== 0 ||
      entry.url === null ||
      resources.get(key) !== entry
    ) {
      return;
    }
    resources.delete(key);
    URL.revokeObjectURL(entry.url);
  });
}

function leaseExistingEntry(key: string, entry: ResourceEntry): AdminImageLease {
  // A replacement consumer keeps the existing authenticated blob or in-flight
  // read. Any queued final-release callback becomes a no-op for this generation.
  entry.revokeGeneration += 1;
  entry.refs += 1;
  let released = false;

  return {
    url: entry.promise,
    release: () => {
      if (released) return;
      released = true;
      entry.refs = Math.max(0, entry.refs - 1);
      scheduleRevokeIfUnused(key, entry);
    },
  };
}

export function acquireAdminImage(token: string, src: string): AdminImageLease {
  const key = resourceKey(token, src);
  let entry = resources.get(key);

  if (!entry) {
    entry = {
      refs: 0,
      url: null,
      promise: Promise.resolve(''),
      revokeGeneration: 0,
    };
    const created = entry;
    created.promise = schedule(() => fetchAdminImage(token, src));
    resources.set(key, created);
    void created.promise.then(
      (url) => {
        created.url = url;
        scheduleRevokeIfUnused(key, created);
      },
      () => {
        if (resources.get(key) === created) resources.delete(key);
      }
    );
  }

  return leaseExistingEntry(key, entry);
}

/**
 * Lease a token-scoped variant only when the pool already owns its resource.
 * This never creates or schedules a read, so an off-screen first mount remains
 * lazy while a replacement table cell can retain a still-visible resource
 * before its task-delivered IntersectionObserver callback arrives.
 */
export function acquireExistingAdminImage(
  token: string,
  src: string
): AdminImageLease | null {
  const key = resourceKey(token, src);
  const entry = resources.get(key);
  return entry ? leaseExistingEntry(key, entry) : null;
}

/** Test-only reset; production has no call site. */
export function resetAdminImageResourcePoolForTests(): void {
  for (const entry of resources.values()) {
    entry.revokeGeneration += 1;
    if (entry.url) URL.revokeObjectURL(entry.url);
  }
  resources.clear();
  waiting.splice(0, waiting.length);
  active = 0;
}
